import React from 'react';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { queryService } from '../services/queryService';
import { useAuth } from '../context/AuthContext';
import { roleConfig } from '../config/roles';
import { generateNextVersion, generateNextVersionFromHistory } from '../utils/versionUtils';
import { useApplicationAccess } from './useApplicationAccess';

export const useQueriesPageController = () => {
    const queryClient = useQueryClient();
    const { hasRole, user } = useAuth();
    const canCreateVersions = hasRole(roleConfig.createVersions);
    const canActivateQueries = hasRole(roleConfig.activateQueries);

    // Filter State
    const [statusFilter, setStatusFilter] = React.useState<'all' | 'active' | 'inactive'>('all');
    const [selectedCategory, setSelectedCategory] = React.useState('All');
    const [searchQuery, setSearchQuery] = React.useState('');
    const [tagSearchQuery, setTagSearchQuery] = React.useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
    const [debouncedTagSearchQuery, setDebouncedTagSearchQuery] = React.useState('');

    // Application access
    const { accessibleApplications, defaultApplicationId } = useApplicationAccess();

    // New Filter State
    const [favoriteOnly, setFavoriteOnly] = React.useState(false);
    const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
    const [debouncedSelectedTags, setDebouncedSelectedTags] = React.useState<string[]>([]);
    // null = not yet initialised (wait for defaultApplicationId), '' = show all apps (admin cleared)
    const [selectedApplicationId, setSelectedApplicationId] = React.useState<string | null>(null);

    // Auto-select the default app once applications have loaded
    React.useEffect(() => {
        if (defaultApplicationId !== null && selectedApplicationId === null) {
            setSelectedApplicationId(defaultApplicationId);
        }
    }, [defaultApplicationId, selectedApplicationId]);

    // Selection State
    const [selectedQueryId, setSelectedQueryId] = React.useState<string | null>(null);
    const [selectedQuery, setSelectedQuery] = React.useState<any>(null);

    // Dialog State
    const [versionDialogOpen, setVersionDialogOpen] = React.useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = React.useState(false);
    const [sqlViewerOpen, setSqlViewerOpen] = React.useState(false);
    const [loadingVersionNumber, setLoadingVersionNumber] = React.useState(false);
    const [loadingHistory, setLoadingHistory] = React.useState(false);

    // Dialog Data
    const [versionForm, setVersionForm] = React.useState({
        versionNumber: '',
        sqlContent: '',
        changeDescription: '',
        createdBy: '',
        queryId: '',
        makeCurrent: true
    });
    const [versionHistory, setVersionHistory] = React.useState<any[]>([]);
    const [selectedVersionSql, setSelectedVersionSql] = React.useState('');
    const [selectedVersionInfo, setSelectedVersionInfo] = React.useState<any>(null);
    const [dialogError, setDialogError] = React.useState<string | null>(null);

    // Snackbar State
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<'success' | 'error' | 'warning' | 'info'>('success');

    // Refs
    const selectedQueryIdRef = React.useRef<string | null>(null);
    const selectedQueryRef = React.useRef<any>(null);
    const versionFormRef = React.useRef<any>(null);

    React.useEffect(() => {
        selectedQueryIdRef.current = selectedQueryId;
    }, [selectedQueryId]);

    React.useEffect(() => {
        selectedQueryRef.current = selectedQuery;
    }, [selectedQuery]);

    React.useEffect(() => {
        versionFormRef.current = versionForm;
    }, [versionForm]);

    // Debounce search
    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedTagSearchQuery(tagSearchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [tagSearchQuery]);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSelectedTags(selectedTags);
        }, 500);
        return () => clearTimeout(handler);
    }, [selectedTags]);

    // --- Queries ---

    // Fetch user favorites
    const { data: favoriteQueryIds = [] } = useQuery({
        queryKey: ['favorites'],
        queryFn: () => queryService.getUserFavorites(),
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    const {
        data: infiniteData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        error
    } = useInfiniteQuery({
        queryKey: ['queries', statusFilter, selectedCategory, debouncedSearchQuery, debouncedTagSearchQuery, favoriteOnly, debouncedSelectedTags, selectedApplicationId],
        queryFn: ({ pageParam = 0 }) => {
            // Combine tag search query and selected tags
            const tagsFromBox = debouncedTagSearchQuery ? debouncedTagSearchQuery.split(',').map(t => t.trim()).filter(Boolean) : [];
            const allTags = [...tagsFromBox, ...debouncedSelectedTags];

            return queryService.searchQueries({
                page: pageParam as number,
                size: 20,
                activeOnly: statusFilter === 'inactive' ? false : true,
                searchTerm: debouncedSearchQuery || undefined,
                category: selectedCategory === 'All' ? undefined : selectedCategory,
                tags: allTags.length > 0 ? allTags : undefined,
                favoriteOnly: favoriteOnly || undefined, // Only send if true
                applicationId: selectedApplicationId || undefined,
                sortBy: 'updatedAt',
                sortDirection: 'DESC'
            });
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            const currentPage = (lastPage as any).number ?? (lastPage as any).page ?? 0;
            const nextPage = currentPage + 1;
            return nextPage < lastPage.totalPages ? nextPage : undefined;
        },
    });

    const queries = React.useMemo(() => {
        const rawQueries = infiniteData?.pages.flatMap(page => page.content || []) || [];
        // Map favorites
        return rawQueries.map((q: any) => ({
            ...q,
            isFavorite: favoriteQueryIds.includes(q.queryId)
        }));
    }, [infiniteData, favoriteQueryIds]);

    const totalElements = infiniteData?.pages[0]?.totalElements || 0;

    const { data: fetchedCategoryCounts = {} } = useQuery({
        queryKey: ['categoryCounts', statusFilter, debouncedSearchQuery, debouncedTagSearchQuery, selectedApplicationId],
        queryFn: () => {
            let activeOnly: boolean | undefined;
            if (statusFilter === 'active') activeOnly = true;
            else if (statusFilter === 'inactive') activeOnly = false;

            return queryService.getCategoryCounts({
                activeOnly,
                searchTerm: debouncedSearchQuery || undefined,
                tags: debouncedTagSearchQuery ? debouncedTagSearchQuery.split(',').map(t => t.trim()).filter(Boolean) : undefined,
                applicationId: selectedApplicationId || undefined,
            });
        }
    });

    const { data: schedules } = useQuery({
        queryKey: ['schedules'],
        queryFn: () => queryService.listSchedules(),
    });

    const categories = React.useMemo(() => {
        const fetchedKeys = Object.keys(fetchedCategoryCounts);
        if (fetchedKeys.length > 0) {
            return ['All', ...fetchedKeys.sort()];
        }
        if (!queries.length) return ['All'];
        const unique = new Set(queries.map((q: any) => q.category).filter(Boolean));
        return ['All', ...Array.from(unique)];
    }, [fetchedCategoryCounts, queries]);

    const categoryCounts = React.useMemo(() => {
        const counts: Record<string, number> = { ...fetchedCategoryCounts };
        const totalFromCounts = Object.values(fetchedCategoryCounts).reduce((sum, count) => sum + count, 0);
        counts['All'] = totalFromCounts || totalElements;
        return counts;
    }, [fetchedCategoryCounts, totalElements]);

    // --- Mutations ---

    const toggleActivation = useMutation({
        mutationFn: ({ queryId, nextActive }: { queryId: string; nextActive: boolean }) =>
            queryService.setQueryActivation(queryId, nextActive),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['queries'] });
        },
    });

    const loadVersionHistory = async (queryId: string) => {
        setLoadingHistory(true);
        try {
            const history = await queryService.getVersionHistory(queryId, 10);
            const historyWithCurrent = history.map(version => ({
                ...version,
                current: version.isCurrent || false
            }));
            setVersionHistory(historyWithCurrent);
        } catch (error) {
            setSnackbarMessage('Failed to load version history');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setVersionHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    const createVersionMutation = useMutation({
        mutationFn: async ({ queryId, versionData }: { queryId: string; versionData: any }) => {
            const newVersion = await queryService.createVersion(queryId, versionData);
            if (versionFormRef.current?.makeCurrent) {
                await queryService.setCurrentVersion(queryId, versionData.versionNumber);
            }
            return newVersion;
        },
        onSuccess: async (newVersionData, variables) => {
            await queryClient.invalidateQueries({ queryKey: ['queries'] });
            await queryClient.refetchQueries({
                queryKey: ['queries'],
                type: 'active',
                exact: false
            });

            setVersionDialogOpen(false);
            setDialogError(null);
            setVersionForm({
                versionNumber: '',
                sqlContent: '',
                changeDescription: '',
                createdBy: user?.name || user?.username || 'Unknown User',
                queryId: '',
                makeCurrent: true
            });

            const actionLabel = variables.versionData.makeCurrent ? 'created and activated!' : 'created!';
            setSnackbarMessage(`✅ Version ${variables.versionData.versionNumber} ${actionLabel}`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            if (historyDialogOpen && selectedQuery) {
                await loadVersionHistory(selectedQuery.queryId);
            }
        },
        onError: (error: any) => {
            console.error('Version creation failed:', error);
            let errorMessage = 'Failed to create version';
            const serverMessage = error?.response?.data?.message || error?.message || '';

            if (serverMessage.includes('duplicate key value') ||
                serverMessage.includes('uk_query_version') ||
                serverMessage.includes('already exists')) {
                const match = serverMessage.match(/\(([^)]+)\)=\(([^)]+)\)/);
                const detail = match ? `(Version ${match[2].split(',')[1].trim()} already exists for this query)` : '';
                errorMessage = `Version conflict: The version number you entered already exists. Please increment the version number and try again. ${detail}`;
            } else {
                errorMessage = serverMessage || 'Failed to create version';
            }

            setDialogError(errorMessage);
        },
    });

    const setCurrentVersionMutation = useMutation({
        mutationFn: ({ queryId, versionNumber }: { queryId: string; versionNumber: string }) =>
            queryService.setCurrentVersion(queryId, versionNumber),
        onSuccess: async (_, variables) => {
            await queryClient.invalidateQueries({ queryKey: ['queries'] });
            setSnackbarMessage(`Version ${variables.versionNumber} is now the current version!`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            if (selectedQuery) {
                await loadVersionHistory(selectedQuery.queryId);
            }
        },
        onError: (error: any) => {
            setSnackbarMessage(`Failed to set current version: ${error.message || 'Unknown error'}`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        },
    });

    // --- Handlers ---

    const handleQuerySelect = (queryId: string | null) => {
        setSelectedQueryId(queryId);
        if (!queryId) {
            setSelectedQuery(null);
            return;
        }
        const query = queries.find(q => q.queryId === queryId);
        if (query) {
            setSelectedQuery(query);
        }
    };

    const handleCreateVersion = async (queryListObject: any) => {
        if (!queryListObject || !queryListObject.queryId) {
            setSnackbarMessage('Error: Cannot create version for invalid query');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        const createdBy = user?.name || user?.username || 'Unknown User';
        setSelectedQuery(queryListObject);
        setSelectedQueryId(queryListObject.queryId);
        setDialogError(null);

        const estimatedNextVersion = generateNextVersion(queryListObject.currentVersion || queryListObject.version);

        setVersionForm({
            versionNumber: estimatedNextVersion,
            sqlContent: queryListObject.sqlContent || '',
            changeDescription: '',
            createdBy,
            queryId: queryListObject.queryId,
            makeCurrent: true
        });

        setLoadingVersionNumber(true);
        setVersionDialogOpen(true);

        try {
            const fullQuery = await queryService.getQuery(queryListObject.queryId);
            const existingVersions = await queryService.getVersionHistory(fullQuery.queryId, 100);
            const nextVersion = generateNextVersionFromHistory(
                existingVersions,
                fullQuery.currentVersion || queryListObject.currentVersion
            );

            setVersionForm(prev => ({
                ...prev,
                versionNumber: nextVersion,
                sqlContent: fullQuery.sqlContent || '',
                queryId: fullQuery.queryId
            }));
        } catch (error) {
            console.error('Failed to load query details or version history:', error);
            setVersionForm({
                versionNumber: generateNextVersion(queryListObject.currentVersion),
                sqlContent: queryListObject.sqlContent || '',
                changeDescription: '',
                createdBy,
                queryId: queryListObject.queryId,
                makeCurrent: true
            });
            setSnackbarMessage('Warning: Could not load full details. creating version based on available data.');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
        } finally {
            setLoadingVersionNumber(false);
        }
    };

    const handleVersionSubmit = () => {
        if (createVersionMutation.isPending) return;
        setDialogError(null);
        const currentForm = versionFormRef.current;
        const targetQueryId = currentForm?.queryId || selectedQueryRef.current?.queryId || selectedQueryIdRef.current;

        if (!targetQueryId) {
            setSnackbarMessage('Error: No query context found. Please re-select the query.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        if (!currentForm?.versionNumber || !currentForm?.sqlContent) {
            setSnackbarMessage('Version Number and SQL Content are required');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        createVersionMutation.mutate({
            queryId: targetQueryId,
            versionData: {
                versionNumber: currentForm.versionNumber,
                sqlContent: currentForm.sqlContent,
                changeNotes: currentForm.changeDescription,
                createdBy: currentForm.createdBy
            }
        });
    };

    const handleVersionDialogClose = () => {
        setVersionDialogOpen(false);
        setSelectedQuery(null);
        setDialogError(null);
        setVersionForm({
            versionNumber: '',
            sqlContent: '',
            changeDescription: '',
            createdBy: user?.name || user?.username || 'Unknown User',
            queryId: '',
            makeCurrent: true
        });
    };

    const handleViewVersionHistory = async (query: any) => {
        setSelectedQuery(query);
        setHistoryDialogOpen(true);
        await loadVersionHistory(query.queryId);
    };

    const handleSetCurrentVersion = (versionNumber: string) => {
        if (setCurrentVersionMutation.isPending) return;
        const currentQuery = selectedQueryRef.current;
        if (!currentQuery) return;
        setCurrentVersionMutation.mutate({
            queryId: currentQuery.queryId,
            versionNumber
        });
    };

    const handleHistoryDialogClose = () => {
        setHistoryDialogOpen(false);
        setSelectedQuery(null);
        setVersionHistory([]);
    };

    const handleViewSql = (version: any) => {
        setSelectedVersionSql(version.sqlContent || '');
        setSelectedVersionInfo(version);
        setSqlViewerOpen(true);
    };

    const handleToggleActivation = (queryId: string, nextActive: boolean) => {
        if (toggleActivation.isPending) return;
        toggleActivation.mutate({ queryId, nextActive });
    };

    const handleApplicationChange = (appId: string) => {
        setSelectedApplicationId(appId);
        setSelectedQueryId(null);
        setSelectedCategory('All');
    };

    // Return everything needed by the UI
    return {
        // State
        searchQuery, setSearchQuery,
        tagSearchQuery, setTagSearchQuery,
        statusFilter, setStatusFilter,
        favoriteOnly, setFavoriteOnly,
        selectedTags, setSelectedTags,
        selectedCategory, setSelectedCategory,
        selectedQueryId, setSelectedQueryId,
        selectedApplicationId, setSelectedApplicationId,
        accessibleApplications,
        selectedQuery,
        versionDialogOpen,
        historyDialogOpen,
        sqlViewerOpen, setSqlViewerOpen,
        loadingVersionNumber,
        loadingHistory,
        versionForm, setVersionForm,
        versionHistory,
        selectedVersionSql, setSelectedVersionSql,
        selectedVersionInfo, setSelectedVersionInfo,
        dialogError,
        snackbarOpen, setSnackbarOpen,
        snackbarMessage,
        snackbarSeverity,

        // Data
        queries,
        totalElements,
        isLoading,
        error,
        isFetchingNextPage,
        categories,
        categoryCounts,
        schedules,

        // Permissions
        canCreateVersions,
        canActivateQueries,

        // Handlers
        fetchNextPage,
        hasNextPage,
        handleQuerySelect,
        handleApplicationChange,
        handleCreateVersion,
        handleVersionSubmit,
        handleVersionDialogClose,
        handleViewVersionHistory,
        handleSetCurrentVersion,
        handleHistoryDialogClose,
        handleViewSql,
        handleSqlViewerClose: () => {
            setSqlViewerOpen(false);
            setSelectedVersionSql('');
            setSelectedVersionInfo(null);
        },

        // Mutations
        toggleActivation,
        handleToggleActivation,
        createVersionMutation,
        setCurrentVersionMutation
    };
};
